import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { UpdateWatcher } from "@/components/UpdateWatcher";
import { AuthProvider } from "@/context/AuthContext";
import { BackNav } from "@/components/BackNav";
import { LangProvider, useLang } from "@/context/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SubscribeGate } from "@/components/SubscribeGate";
import { ProjectProvider } from "@/context/ProjectContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteScrollTop from "@/components/RouteScrollTop";
import FeedbackButton from "@/components/FeedbackButton";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Projects from "@/pages/Projects";
import StrategyGenerator from "@/pages/StrategyGenerator";
import Strategies from "@/pages/Strategies";
import Goals from "@/pages/Goals";
import ContentStudio from "@/pages/ContentStudio";
import CampaignScheduler from "@/pages/CampaignScheduler";
import ContentLibrary from "@/pages/ContentLibrary";
import Measurement from "@/pages/Measurement";
import Analytics from "@/pages/Analytics";
import Packages from "@/pages/Packages";
import BlogAdmin from "@/pages/BlogAdmin";
import Reports from "@/pages/Reports";
import ReportView from "@/pages/ReportView";
import Workspace from "@/pages/Workspace";
import AdminOverview from "@/pages/AdminOverview";
import PaymentResult from "@/pages/PaymentResult";
import SocialHub from "@/pages/SocialHub";
import WhatsAppBot from "@/pages/WhatsAppBot";
import EmailBot from "@/pages/EmailBot";
import DataDeletion from "@/pages/DataDeletion";
import DigitalCard from "@/pages/DigitalCard";
import { BlogList, BlogPost } from "@/pages/Blog";
import Samples from "@/pages/Samples";
import Guide from "@/pages/Guide";
import { NoCreditDialog } from "@/components/NoCreditDialog";
import { PrivacyPolicy, TermsOfService, RefundPolicy, SecurityPolicy, LiabilityPolicy } from "@/pages/Legal";

function Shell({ children }) {
  const { dir } = useLang();
  return <div className="App" dir={dir} style={{ textAlign: "start" }}>{children}</div>;
}

function DirToaster() {
  const { dir } = useLang();
  return <Toaster richColors position="top-center" dir={dir} />;
}

const DashboardAlias = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/^\/dashboard/, "/app") + search} replace />;
};

function App() {
  return (
    <ThemeProvider>
    <LangProvider>
    <Shell>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SubscribeGate />
            <ProjectProvider>
              <RouteScrollTop />
              <FeedbackButton />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/samples" element={<Samples />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/app/report/:projectId" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/card/:id" element={<DigitalCard />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/liability" element={<LiabilityPolicy />} />
                <Route path="/security" element={<SecurityPolicy />} />
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Workspace />} />
                  <Route path="workspace" element={<Navigate to="/app" replace />} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="strategy" element={<StrategyGenerator />} />
                  <Route path="strategies" element={<Strategies />} />
                  <Route path="goals" element={<Goals />} />
                  <Route path="studio" element={<ContentStudio />} />
                  <Route path="campaigns" element={<CampaignScheduler />} />
                  <Route path="library" element={<ContentLibrary />} />
                  <Route path="measurement" element={<Measurement />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="social" element={<SocialHub />} />
                  <Route path="whatsapp-bot" element={<WhatsAppBot />} />
                  <Route path="email-bot" element={<EmailBot />} />
                  <Route path="packages" element={<Packages />} />
                  <Route path="payment-result" element={<PaymentResult />} />
                  <Route path="blog-admin" element={<BlogAdmin />} />
                  <Route path="admin-overview" element={<AdminOverview />} />
                </Route>
                <Route path="/dashboard/*" element={<DashboardAlias />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <BackNav />
              <NoCreditDialog />
              <DirToaster />
              <UpdateWatcher />
            </ProjectProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </Shell>
    </LangProvider>
    </ThemeProvider>
  );
}

export default App;
